import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Tooltip,
  Skeleton,
  Collapse,
  Fab,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  PlusIcon,
  FunnelIcon,
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  NoSymbolIcon,
  GiftIcon,
  ReceiptPercentIcon,
  UserGroupIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useActionParam from '../../hooks/useActionParam';
import { useFilterParams } from '../../hooks/useFilterParams';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useAuth } from '../../context/AuthContext';
import { financeApi } from '../../api/financeApi';
import { formatCurrency, CURRENCY } from '../../config/currency';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import FinanceDateRange from '../../components/finance/FinanceDateRange';
import FilteredTotals from '../../components/finance/FilteredTotals';
import {
  PrimaryButton,
  SecondaryButton,
  AppIconButton,
  PageHeroBand,
  SearchFilterBar,
  DataTable,
  StatusChip,
  EmptyState,
} from '../../components/ui';
import type { FilterConfig, DataTableColumn } from '../../components/ui';
import { thisMonthRange, lastMonthRange, todayLocalIso } from '../../utils/dateLocal';
import { useFuzzy } from '../../utils/fuzzySearch';
import ExpenseForm from '../../components/finance/ExpenseForm';
import type { ExpenseFormData, ExpenseCategory, StaffMember } from '../../components/finance/ExpenseForm';
import NonRevenueCostForm from '../../components/finance/NonRevenueCostForm';
import InvoiceScanner from '../../components/finance/InvoiceScanner';
import OcrReviewForm from '../../components/finance/OcrReviewForm';
import type { OcrResultData } from '../../components/finance/InvoiceScanner';
import { downloadBlob } from '../../utils/downloadBlob';

// Compact "enterprise" table density (Item 9): tighter cells, uppercase
// tertiary header row, token-driven row hover.
const denseTableSx = {
  '& .MuiTableCell-root': { padding: '8px 12px' },
  '& .MuiTableCell-head': {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'var(--text-tertiary)',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: 'var(--bg-hover)',
  },
} as const;

// --- Non-Revenue Cost Types ---

interface NonRevenueCost {
  id: number;
  type: 'VOID' | 'COMPLIMENTARY' | 'STAFF_MEAL';
  estimatedValue: number;
  reason: string | null;
  date: string;
  createdAt: string;
}

const NRC_TYPE_STYLES: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  VOID: { label: 'Void', color: '#D99982', icon: NoSymbolIcon },
  COMPLIMENTARY: { label: 'Complimentary', color: 'var(--text-link)', icon: GiftIcon },
  STAFF_MEAL: { label: 'Staff Meal', color: 'var(--status-ok)', icon: UserGroupIcon },
};

// --- Types ---

interface Expense {
  id: number;
  categoryId: number;
  categoryName: string;
  amount: number;
  vatAmount: number | null;
  date: string;
  fromDate?: string | null;
  toDate?: string | null;
  recordedDate?: string | null;
  paymentStatus: 'PAID' | 'UNPAID' | 'PARTIALLY_PAID';
  paymentMode: string;
  supplierName: string;
  dueDate: string | null;
  staffMemberId: number | null;
  staffMemberName: string | null;
  description: string | null;
  imageKey: string | null;
  imageUrl: string | null;
  createdAt: string;
}

interface ExpenseListResponse {
  content: Expense[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface Filters {
  categoryId: number | '';
  paymentStatus: string;
  supplierSearch: string;
  dateFrom: string;
  dateTo: string;
  // Drill-down from Finance Overview "Unspecified" payee bar — restricts to
  // rows missing BOTH supplier and category so user can assign them inline.
  unspecified: boolean;
}

// --- Constants ---

const PAYMENT_STATUS_MAP: Record<string, { label: string; variant: 'success' | 'warning' | 'error' }> = {
  PAID: { label: 'Paid', variant: 'success' },
  UNPAID: { label: 'Unpaid', variant: 'error' },
  PARTIALLY_PAID: { label: 'Partial', variant: 'warning' },
};

const PAYMENT_MODE_LABELS: Record<string, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank',
  UPI: 'UPI',
  CARD: 'Card',
  CREDIT: 'Credit',
};

const getThisMonthRange = () => thisMonthRange();

const DEFAULT_FILTERS: Filters = {
  categoryId: '',
  paymentStatus: '',
  supplierSearch: '',
  ...getThisMonthRange(),
  unspecified: false,
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

// Categories that must be managed via Staff page (not the Expenses form).
// Editing one redirects to /vendor/finance/staff with the payment pre-loaded;
// creating one is blocked from the expense form entirely.
const STAFF_PAYMENT_CATEGORIES = new Set([
  'Salaries & Wages',
  'Staff Advance',
  'Staff Bonus',
  'Staff Overtime',
  'Staff Deduction',
]);

// --- Component ---

const ExpenseManagement: React.FC = () => {
  const { theme } = useTheme();
  const confirm = useConfirm();
  const { user } = useAuth();
  // Finance-only-restricted stewards can ADD entries but cannot edit or delete
  // — the affordances are removed from the UI here, and the backend
  // double-enforces with a 403 if a request leaks through.
  const isFinanceOnlyRestricted = Boolean(user?.financeOnlySteward);
  // Deep-link: dashboard Quick Action opens Add Expense (respects gating).
  useActionParam('add', !isFinanceOnlyRestricted, () => handleOpenAddRef.current?.());
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  // Data state
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  // TODO: Fetch staff members from API when staff payment feature is built (US5)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // URL-synced filters — survive back-button navigation from detail pages.
  // categoryId stored as string ('' for none) in URL; converted to number for API.
  const URL_FILTER_DEFAULTS = useMemo(
    () => ({
      categoryId: '' as string,
      paymentStatus: DEFAULT_FILTERS.paymentStatus,
      supplierSearch: DEFAULT_FILTERS.supplierSearch,
      dateFrom: DEFAULT_FILTERS.dateFrom,
      dateTo: DEFAULT_FILTERS.dateTo,
      unspecified: '' as string,
    }),
    [],
  );
  const [urlFilters, setUrlFilters] = useFilterParams(URL_FILTER_DEFAULTS, { prefix: 'exp_' });
  const filters: Filters = useMemo(
    () => ({
      categoryId:
        urlFilters.categoryId && /^\d+$/.test(urlFilters.categoryId)
          ? Number(urlFilters.categoryId)
          : '',
      paymentStatus: urlFilters.paymentStatus,
      supplierSearch: urlFilters.supplierSearch,
      dateFrom: urlFilters.dateFrom,
      dateTo: urlFilters.dateTo,
      unspecified: urlFilters.unspecified === 'true',
    }),
    [urlFilters],
  );
  const setFilters = useCallback(
    (next: Filters | ((prev: Filters) => Filters)) => {
      const value = typeof next === 'function' ? next(filters) : next;
      setUrlFilters({
        categoryId: value.categoryId === '' ? '' : String(value.categoryId),
        paymentStatus: value.paymentStatus,
        supplierSearch: value.supplierSearch,
        dateFrom: value.dateFrom,
        dateTo: value.dateTo,
        unspecified: value.unspecified ? 'true' : '',
      });
    },
    [setUrlFilters, filters],
  );
  // One-time hydration from legacy deep-link params (Finance Overview pencil
  // sends categoryId / supplierName / dateFrom / dateTo without prefix).
  // Migrate them into prefixed keys on first render so back-button behavior works.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated) return;
    setHydrated(true);
    const patch: Partial<typeof URL_FILTER_DEFAULTS> = {};
    const cat = searchParams.get('categoryId');
    if (cat && /^\d+$/.test(cat)) patch.categoryId = cat;
    const sup = searchParams.get('supplierName');
    if (sup) patch.supplierSearch = sup;
    const df = searchParams.get('dateFrom');
    if (df) patch.dateFrom = df;
    const dt = searchParams.get('dateTo');
    if (dt) patch.dateTo = dt;
    const uns = searchParams.get('unspecified');
    if (uns === 'true') patch.unspecified = 'true';
    if (Object.keys(patch).length > 0) {
      // Use a single functional setSearchParams update — calling setUrlFilters
      // followed by a second setSearchParams race-condition'd the second call
      // (built from a stale snapshot) into stomping the just-written prefixed keys.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => {
            if (v !== undefined && v !== '') next.set(`exp_${k}`, String(v));
          });
          ['categoryId', 'supplierName', 'dateFrom', 'dateTo', 'unspecified'].forEach((k) => next.delete(k));
          return next;
        },
        { replace: true },
      );
    }
  }, [hydrated, searchParams, setSearchParams]);
  const [showFilters, setShowFilters] = useState(() => {
    try { return localStorage.getItem('finance_expense_filters_open') === '1'; } catch { return false; }
  });

  const toggleFilters = () => {
    setShowFilters((prev) => {
      const next = !prev;
      try { localStorage.setItem('finance_expense_filters_open', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.categoryId !== '') n++;
    if (filters.paymentStatus !== '') n++;
    if (filters.supplierSearch !== '') n++;
    if (filters.dateFrom !== '' || filters.dateTo !== '') n++;
    if (filters.unspecified) n++;
    return n;
  }, [filters]);
  const editFromUrl = searchParams.get('edit');
  const [pendingEditId, setPendingEditId] = useState<number | null>(
    editFromUrl && /^\d+$/.test(editFromUrl) ? Number(editFromUrl) : null,
  );

  // Modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseFormData | null>(null);

  // Staff payment categories are managed from the Staff page, not here.
  // Hide them from the expense form's category dropdown.
  const expenseFormCategories = useMemo(
    () => categories.filter(
      (c) => !c.isStaffCategory && !STAFF_PAYMENT_CATEGORIES.has(c.name)
    ),
    [categories]
  );

  // Tab state: "expenses" | "voids" | "recurring"
  const [activeTab, setActiveTab] = useState<'expenses' | 'voids' | 'recurring'>('expenses');

  // Non-revenue cost state
  const [nrcList, setNrcList] = useState<NonRevenueCost[]>([]);
  const [nrcLoading, setNrcLoading] = useState(false);
  const [nrcFormOpen, setNrcFormOpen] = useState(false);

  // Scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [ocrData, setOcrData] = useState<OcrResultData | null>(null);

  // Recurring templates state
  interface RecurringTemplate {
    id: number;
    description: string;
    amount: number;
    frequency: 'WEEKLY' | 'BI_WEEKLY' | 'MONTHLY';
    repeatDay: number;
    categoryName: string;
    status: 'ACTIVE' | 'PAUSED' | 'CANCELLED';
    nextDueDate: string | null;
    supplierName: string | null;
  }
  const [recurringList, setRecurringList] = useState<RecurringTemplate[]>([]);
  const [recurringLoading, setRecurringLoading] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);

  // --- Summary (from server, not client-side page sum) ---
  const [serverSummary, setServerSummary] = useState<{ totalAmount: number; count: number }>({ totalAmount: 0, count: 0 });
  const summary = useMemo(() => ({
    total: serverSummary.totalAmount,
    count: serverSummary.count || totalElements,
    totalAll: totalElements,
  }), [serverSummary, totalElements]);

  // --- Client-side fuzzy search ---
  // When supplierSearch is set, fetchExpenses pulls a larger page without the
  // backend supplierName filter so typo searches (e.g. "bataty" → "Al-Batati")
  // still match.
  const fuzzyExpenseKeys = useMemo(
    () => ['supplierName', 'description', 'paymentMode', 'categoryName'],
    [],
  );
  const fuzzyExpenseMatches = useFuzzy<Expense>(
    expenses,
    fuzzyExpenseKeys,
    filters.supplierSearch,
    { threshold: 0.4 },
  );
  const expenseSearchActive = !!filters.supplierSearch.trim();
  const displayedExpenses = expenseSearchActive ? fuzzyExpenseMatches : expenses;
  const displayedExpenseTotal = expenseSearchActive ? displayedExpenses.length : totalElements;
  const displayedExpenseSum = useMemo(() => {
    if (!expenseSearchActive) return summary.total;
    return displayedExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  }, [expenseSearchActive, displayedExpenses, summary.total]);

  const hasActiveFilters = useMemo(
    () =>
      filters.categoryId !== '' ||
      filters.paymentStatus !== '' ||
      filters.supplierSearch !== '' ||
      filters.dateFrom !== '' ||
      filters.dateTo !== '' ||
      filters.unspecified,
    [filters]
  );

  // --- Data Fetching ---

  // Race guard — overlapping fetches (e.g. init fetch + post-hydration refetch
  // when arriving via a deep-link from Top Payees) can resolve out of order
  // and overwrite the filtered result with stale unfiltered rows.
  const fetchSeqRef = useRef(0);
  const fetchExpenses = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    try {
      const searchActive = !!filters.supplierSearch;
      const params: Record<string, unknown> = {
        page: searchActive ? 0 : page,
        size: searchActive ? 500 : rowsPerPage,
      };
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
      // When search active we fetch broader and apply fuzzy client-side (typo
      // tolerance). Skip backend `supplierName` filter so we don't pre-narrow.
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.unspecified) params.unspecified = true;

      const res = await financeApi.getExpenses(params);
      if (seq !== fetchSeqRef.current) return; // stale response — discard
      const data: ExpenseListResponse = res.data;

      // Support both paginated (Spring Page) and array responses
      if (Array.isArray(res.data)) {
        setExpenses(res.data);
        setTotalElements(res.data.length);
      } else {
        setExpenses(data.content || []);
        setTotalElements(data.totalElements || 0);
        // Extract server-side summary (total across ALL pages, not just current page)
        if (data.summary) {
          setServerSummary({
            totalAmount: data.summary.totalAmount || 0,
            count: data.summary.count || data.totalElements || 0,
          });
        }
      }
    } catch (err: unknown) {
      if (seq !== fetchSeqRef.current) return;
      console.error('Error fetching expenses:', err);
      toast.error('Failed to load expenses');
    }
  }, [page, rowsPerPage, filters]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await financeApi.getCategories();
      const cats = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setCategories(cats);
    } catch (err: unknown) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  // Initial load — wait for legacy-param hydration so a deep-link landing
  // (e.g. ?supplierName=X / ?unspecified=true from Top Payees) doesn't fire
  // an unfiltered fetch first and lose the race against the filtered one.
  useEffect(() => {
    if (!hydrated) return;
    let cancelled = false;
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchExpenses(), fetchCategories()]);
      if (!cancelled) setLoading(false);
    };
    init();
    return () => { cancelled = true; };
  }, [hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when page, pageSize, or filters change (but not on initial load)
  useEffect(() => {
    if (!loading) {
      fetchExpenses();
    }
  }, [page, rowsPerPage, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open edit modal when ?edit=<id> is in the URL (deep-link from Finance Overview pencil).
  // Runs after expenses load; clears the param so back-nav doesn't re-trigger it.
  useEffect(() => {
    if (pendingEditId == null || loading || expenses.length === 0) return;
    const target = expenses.find((e) => e.id === pendingEditId);
    if (target) {
      handleOpenEdit(target);
      setPendingEditId(null);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [pendingEditId, loading, expenses]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchExpenses();
    setRefreshing(false);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    const toastId = toast.loading('Exporting...');
    try {
      const params: Record<string, unknown> = { format: 'CSV' };
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
      if (filters.supplierSearch) params.supplierSearch = filters.supplierSearch;
      if (filters.unspecified) params.unspecified = true;
      const res = await financeApi.exportData('expenses', params);
      const filename = `expenses-${todayLocalIso()}.csv`;
      downloadBlob(res.data, filename);
      toast.success('Downloaded!', { id: toastId });
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error('Export failed', { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  // --- Non-Revenue Cost Fetching ---

  const fetchNonRevenueCosts = useCallback(async () => {
    setNrcLoading(true);
    try {
      const res = await financeApi.getNonRevenueCosts();
      const list = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setNrcList(list);
    } catch (err: unknown) {
      console.error('Error fetching non-revenue costs:', err);
      toast.error('Failed to load voids & comps');
    } finally {
      setNrcLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'voids') {
      fetchNonRevenueCosts();
    }
  }, [activeTab, fetchNonRevenueCosts]);

  // --- Recurring Templates Fetching ---

  const fetchRecurringTemplates = useCallback(async () => {
    setRecurringLoading(true);
    try {
      const res = await financeApi.getRecurringTemplates();
      const list = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setRecurringList(list);
    } catch (err: unknown) {
      console.error('Error fetching recurring templates:', err);
      toast.error('Failed to load recurring expenses');
    } finally {
      setRecurringLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'recurring') {
      fetchRecurringTemplates();
    }
  }, [activeTab, fetchRecurringTemplates]);

  const handleRecurringAction = async (action: 'pause' | 'resume' | 'cancel', id: number) => {
    const toastId = toast.loading(
      action === 'pause' ? 'Pausing...' : action === 'resume' ? 'Resuming...' : 'Cancelling...'
    );
    try {
      if (action === 'pause') await financeApi.pauseRecurringTemplate(id);
      else if (action === 'resume') await financeApi.resumeRecurringTemplate(id);
      else await financeApi.cancelRecurringTemplate(id);
      toast.success(
        action === 'pause' ? 'Paused' : action === 'resume' ? 'Resumed' : 'Cancelled',
        { id: toastId }
      );
      await fetchRecurringTemplates();
    } catch {
      toast.error('Action failed', { id: toastId });
    }
  };

  const nrcSummary = useMemo(() => {
    const byType: Record<string, number> = { VOID: 0, COMPLIMENTARY: 0, STAFF_MEAL: 0 };
    let total = 0;
    nrcList.forEach((nrc) => {
      byType[nrc.type] = (byType[nrc.type] || 0) + nrc.estimatedValue;
      total += nrc.estimatedValue;
    });
    return { byType, total };
  }, [nrcList]);

  // --- Scanner handlers ---

  const handleScanResult = useCallback((data: OcrResultData) => {
    setOcrData(data);
    setScannerOpen(false);
    setReviewOpen(true);
  }, []);

  const handleOcrSaved = useCallback(() => {
    setReviewOpen(false);
    setOcrData(null);
    fetchExpenses();
  }, [fetchExpenses]);

  // --- CRUD handlers ---

  const handleOpenAddRef = React.useRef<() => void>();
  const handleOpenAdd = () => {
    setEditingExpense(null);
    setFormOpen(true);
  };
  handleOpenAddRef.current = handleOpenAdd;

  const handleOpenEdit = (expense: Expense) => {
    // Staff-category expenses (Salaries & Wages, Staff Advance, etc.) are
    // managed from the Staff page. Redirect there with the payment context
    // so the edit modal can open pre-filled.
    const categoryName = expense.categoryName
      || categories.find((c) => c.id === expense.categoryId)?.name;
    if (categoryName && STAFF_PAYMENT_CATEGORIES.has(categoryName)) {
      navigate('/vendor/finance/staff', {
        state: {
          editPayment: {
            id: expense.id,
            amount: expense.amount,
            date: expense.date,
            paymentMode: expense.paymentMode,
            categoryName,
            categoryId: expense.categoryId,
            staffMemberId: expense.staffMemberId,
            salaryMonth: (expense as unknown as { salaryMonth?: string }).salaryMonth ?? null,
            salaryFrequency: (expense as unknown as { salaryFrequency?: string }).salaryFrequency ?? null,
            salaryWeek: (expense as unknown as { salaryWeek?: number }).salaryWeek ?? null,
            description: expense.description,
          },
        },
      });
      return;
    }
    setEditingExpense({
      id: expense.id,
      categoryId: expense.categoryId,
      amount: expense.amount,
      vatAmount: expense.vatAmount ?? '',
      date: expense.date,
      // Hydrate the new range / recorded fields. Old rows only have `date`,
      // so the form will fall back to that for `fromDate`.
      fromDate: expense.fromDate || expense.date,
      toDate: expense.toDate || '',
      recordedDate: expense.recordedDate || '',
      paymentStatus: expense.paymentStatus,
      paymentMode: expense.paymentMode as ExpenseFormData['paymentMode'],
      supplierName: expense.supplierName || '',
      dueDate: expense.dueDate || '',
      staffMemberId: expense.staffMemberId || '',
      description: expense.description || '',
      imageKey: expense.imageKey || undefined,
      imageUrl: expense.imageUrl || undefined,
    });
    setFormOpen(true);
  };

  const handleSave = async (formData: ExpenseFormData) => {
    const payload: Record<string, unknown> = {
      categoryId: formData.categoryId,
      amount: Number(formData.amount),
      vatAmount: formData.vatAmount === '' || formData.vatAmount === undefined || formData.vatAmount === null
        ? null
        : Number(formData.vatAmount),
      // Send the new range fields. Backend uses fromDate as the canonical
      // single-day date when toDate is null, so we keep `date` mirrored for
      // any code path that still reads it.
      fromDate: formData.fromDate,
      toDate: formData.toDate || null,
      recordedDate: formData.recordedDate || null,
      date: formData.fromDate,
      paymentStatus: formData.paymentStatus,
      paymentMode: formData.paymentMode,
      supplierName: formData.supplierName || null,
      dueDate: formData.dueDate || null,
      staffMemberId: formData.staffMemberId || null,
      description: formData.description || null,
      imageKey: formData.imageKey || null,
    };

    if (formData.id) {
      await financeApi.updateExpense(formData.id, payload);

      // Check if there are similar expenses with different categories — offer bulk update
      if (formData.supplierName && formData.categoryId) {
        const supplierKey = formData.supplierName.split(' - ')[0].split(',')[0].trim();
        try {
          const countRes = await financeApi.getExpenses({ supplierName: supplierKey, size: 1 });
          const totalSimilar = countRes.data?.totalElements || 0;
          if (totalSimilar > 1) {
            const categoryName = categories.find(c => c.id === formData.categoryId)?.name || 'this category';
            toast((t) => (
              <span style={{ fontSize: '0.85rem' }}>
                <strong>{totalSimilar} expenses</strong> match &ldquo;{supplierKey}&rdquo;.
                Update all to <strong>{categoryName}</strong>?
                <br />
                <button
                  onClick={async () => {
                    toast.dismiss(t.id);
                    try {
                      const res = await financeApi.bulkUpdateExpenseCategory(supplierKey, formData.categoryId!);
                      toast.success(`Updated ${res.data?.updatedCount || 0} expenses`);
                      await fetchExpenses();
                    } catch { toast.error('Bulk update failed'); }
                  }}
                  style={{ marginRight: 8, marginTop: 6, padding: '4px 12px', borderRadius: 6, border: 'none', backgroundColor: 'var(--action)', color: 'var(--text-inverse)', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem' }}
                >
                  Yes, update all
                </button>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  style={{ marginTop: 6, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--border-default)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.8rem' }}
                >
                  No, just this one
                </button>
              </span>
            ), { duration: 10000 });
          } else {
            toast.success('Expense updated');
          }
        } catch {
          toast.success('Expense updated');
        }
      } else {
        toast.success('Expense updated');
      }
    } else {
      await financeApi.createExpense(payload);
      toast.success('Expense added');
    }
    await fetchExpenses();
  };

  const handleDelete = async (expense: Expense) => {
    const confirmed = await confirm({
      title: 'Delete Expense',
      description: `Delete this ${formatCurrency(expense.amount)} expense${expense.supplierName ? ` from ${expense.supplierName}` : ''}? This can be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    try {
      await financeApi.deleteExpense(expense.id);
      toast.success('Expense deleted', {
        duration: 5000,
      });
      await fetchExpenses();
    } catch (err: unknown) {
      console.error('Error deleting expense:', err);
      toast.error('Failed to delete expense');
    }
  };

  // --- Filter handlers ---

  const handleFilterChange = (field: keyof Filters, value: unknown) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  // --- Render helpers ---

  const getModeLabel = (mode: string) => PAYMENT_MODE_LABELS[mode] || mode;

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(CURRENCY.locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const getCategoryName = (expense: Expense) =>
    expense.categoryName || categories.find((c) => c.id === expense.categoryId)?.name || 'Uncategorized';

  // --- SearchFilterBar config for expanded filters ---

  const expandedFilterConfigs: FilterConfig[] = useMemo(() => [
    {
      key: 'categoryId',
      label: 'Category',
      options: [
        { value: '', label: 'All' },
        ...categories.map((cat) => ({ value: String(cat.id), label: cat.name })),
      ],
      value: String(filters.categoryId),
      onChange: (value: string) => handleFilterChange('categoryId', value === '' ? '' : Number(value)),
      minWidth: 160,
    },
    {
      key: 'paymentStatus',
      label: 'Status',
      options: [
        { value: '', label: 'All' },
        { value: 'PAID', label: 'Paid' },
        { value: 'UNPAID', label: 'Unpaid' },
        { value: 'PARTIALLY_PAID', label: 'Partial' },
      ],
      value: filters.paymentStatus,
      onChange: (value: string) => handleFilterChange('paymentStatus', value),
      minWidth: 150,
    },
  ], [categories, filters.categoryId, filters.paymentStatus]);

  // --- DataTable columns ---

  const expenseColumns: DataTableColumn<Expense>[] = useMemo(() => [
    {
      id: 'date',
      label: 'Date',
      render: (row) => (
        <Typography sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem', whiteSpace: 'nowrap' }}>
          {formatDate(row.date)}
        </Typography>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      render: (row) => (
        <Chip
          label={getCategoryName(row)}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            backgroundColor: theme.button.primary.bg,
            color: theme.button.primary.text,
            height: 24,
          }}
        />
      ),
    },
    {
      id: 'supplier',
      label: 'Supplier',
      render: (row) =>
        row.supplierName ? (
          <Typography sx={{ color: theme.text, fontSize: '0.875rem' }}>{row.supplierName}</Typography>
        ) : (
          <Typography component="span" sx={{ color: theme.gray?.[300] || '#CCC', fontStyle: 'italic', fontSize: '0.813rem' }}>
            --
          </Typography>
        ),
    },
    {
      id: 'amount',
      label: 'Amount',
      align: 'right' as const,
      render: (row) => (
        <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
          {formatCurrency(row.amount, { showDecimals: false })}
        </Typography>
      ),
    },
    {
      id: 'vatAmount',
      label: 'VAT',
      align: 'right' as const,
      minWidth: 80,
      render: (row) =>
        row.vatAmount && Number(row.vatAmount) > 0 ? (
          <Typography sx={{ fontWeight: 600, color: theme.gray?.[500] || '#666', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
            {formatCurrency(row.vatAmount, { showDecimals: false })}
          </Typography>
        ) : (
          <Typography component="span" sx={{ color: theme.gray?.[300] || '#CCC', fontSize: '0.875rem' }}>
            —
          </Typography>
        ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => {
        const statusInfo = PAYMENT_STATUS_MAP[row.paymentStatus] || { label: row.paymentStatus, variant: 'info' as const };
        return (
          <StatusChip
            status={statusInfo.label}
            variant={statusInfo.variant}
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        );
      },
    },
    {
      id: 'mode',
      label: 'Mode',
      render: (row) => (
        <Chip
          label={getModeLabel(row.paymentMode)}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: '0.7rem',
            backgroundColor: theme.gray?.[100] || '#F0F0F0',
            color: theme.gray?.[500] || '#666',
            height: 24,
          }}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right' as const,
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
          {!isFinanceOnlyRestricted && (
            <>
              <AppIconButton
                icon={<PencilSquareIcon style={{ width: 16, height: 16 }} />}
                tooltip="Edit"
                variant="ghost"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(row);
                }}
              />
              <AppIconButton
                icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                tooltip="Delete"
                variant="ghost"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row);
                }}
                sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' } }}
              />
            </>
          )}
        </Box>
      ),
    },
  ], [theme, categories, isFinanceOnlyRestricted]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Skeleton loaders ---

  const renderSkeletonCards = () =>
    Array.from({ length: 4 }).map((_, i) => (
      <Paper
        key={i}
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: '12px',
          backgroundColor: theme.components?.card?.background || '#FFFFFF',
          border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Skeleton variant="rounded" width={100} height={24} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
          <Skeleton variant="rounded" width={80} height={28} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Skeleton variant="text" width={120} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
            <Skeleton variant="text" width={80} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
          </Box>
          <Skeleton variant="rounded" width={56} height={24} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
        </Box>
      </Paper>
    ));


  // --- Render ---

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh', pb: { xs: 'calc(96px + env(safe-area-inset-bottom, 0px))', sm: 3 } }}>
      <FinanceTabBar />

      {/* Header — confident-shell hero band */}
      <PageHeroBand
        eyebrow="Finance · Expenses"
        title="Expenses"
        heroLabel="Filtered total"
        heroValue={formatCurrency(displayedExpenseSum, { showDecimals: false })}
        actions={
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              gap: 1.5,
              flexShrink: 0,
              // Outlined (secondary) buttons wash over the dark band; the
              // primary CTA keeps its saffron fill (good contrast on band).
              '& .MuiButton-outlined': {
                backgroundColor: 'color-mix(in srgb, var(--band-text) 10%, transparent)',
                color: 'var(--band-text)',
                borderColor: 'color-mix(in srgb, var(--band-text) 25%, transparent)',
                '&:hover': {
                  backgroundColor: 'color-mix(in srgb, var(--band-text) 18%, transparent)',
                  borderColor: 'color-mix(in srgb, var(--band-text) 40%, transparent)',
                },
              },
            }}
          >
            {/* Bank-import + OCR scanner are vendor-only; hide for restricted stewards. */}
            {!isFinanceOnlyRestricted && (
              <SecondaryButton
                startIcon={<ArrowUpTrayIcon style={{ width: 20, height: 20, color: 'inherit' }} />}
                onClick={() => navigate('/vendor/finance/import')}
                sx={{
                  borderRadius: '10px',
                  px: '20px',
                  fontSize: '0.875rem',
                }}
              >
                Import
              </SecondaryButton>
            )}
            {!isFinanceOnlyRestricted && (
              <SecondaryButton
                startIcon={<DocumentTextIcon style={{ width: 20, height: 20, color: 'inherit' }} />}
                onClick={() => setScannerOpen(true)}
                sx={{
                  borderRadius: '10px',
                  px: '20px',
                  fontSize: '0.875rem',
                }}
              >
                Scan Invoice
              </SecondaryButton>
            )}
            <PrimaryButton
              startIcon={<PlusIcon style={{ width: 20, height: 20 }} />}
              onClick={handleOpenAdd}
              data-testid="add-expense-button"
              sx={{
                borderRadius: '10px',
                boxShadow: '0 2px 8px color-mix(in srgb, var(--action) 30%, transparent)',
                '&:hover': {
                  boxShadow: '0 4px 12px color-mix(in srgb, var(--action) 40%, transparent)',
                },
              }}
            >
              Add Expense
            </PrimaryButton>
          </Box>
        }
      />

      {/* Tab Selector — Voids & Comps and Recurring are vendor-only flows
          (require edit/delete + recurring template management); hide for
          finance-only restricted stewards. */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: { xs: 2, sm: 3 } }}>
        {([
          { key: 'expenses' as const, label: 'Expenses' },
          { key: 'voids' as const, label: 'Voids & Comps' },
          { key: 'recurring' as const, label: 'Recurring' },
        ].filter((t) => !isFinanceOnlyRestricted || t.key === 'expenses')).map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <Chip
              key={tab.key}
              label={tab.label}
              onClick={() => setActiveTab(tab.key)}
              sx={{
                fontWeight: 600,
                fontSize: '0.813rem',
                height: 36,
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                backgroundColor: isActive ? `${theme.primary}22` : theme.gray?.[100] || '#F0F0F0',
                color: isActive ? theme.primary : theme.gray?.[500] || '#666',
                border: isActive ? `1.5px solid ${theme.primary}` : `1px solid transparent`,
                '&:hover': { backgroundColor: isActive ? `${theme.primary}30` : theme.gray?.[200] || '#E0E0E0' },
              }}
            />
          );
        })}
      </Box>

      {activeTab === 'expenses' && (
      <>
      {/* Filter Bar */}
      <Paper
        sx={{
          mb: { xs: 2, sm: 3 },
          borderRadius: '12px',
          backgroundColor: theme.components?.card?.background || '#FFFFFF',
          border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
          overflow: 'hidden',
        }}
      >
        {/* Filter chip row — always visible */}
        <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tooltip title={showFilters ? 'Hide filters' : 'Show filters'}>
            <button
              onClick={toggleFilters}
              data-testid="filter-toggle-btn"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 16,
                border: showFilters || activeFilterCount > 0 ? `1.5px solid ${theme.primary || 'var(--action)'}` : `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                backgroundColor: showFilters || activeFilterCount > 0 ? `${theme.primary || 'var(--action)'}20` : theme.gray?.[100] || '#F0F0F0',
                color: showFilters || activeFilterCount > 0 ? theme.primary || 'var(--action)' : theme.gray?.[500] || '#666',
                fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit',
                minHeight: 32, whiteSpace: 'nowrap',
              }}
            >
              <FunnelIcon style={{ width: 14, height: 14 }} />
              Filters
              {activeFilterCount > 0 && (
                <span style={{
                  backgroundColor: theme.primary || 'var(--action)',
                  color: 'var(--text-inverse)',
                  borderRadius: 10,
                  padding: '0 6px',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  minWidth: 16,
                  textAlign: 'center',
                }}>{activeFilterCount}</span>
              )}
            </button>
          </Tooltip>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 16,
                border: `1px solid ${theme.danger || '#D99982'}50`,
                backgroundColor: `${theme.danger || '#D99982'}15`,
                color: theme.danger || '#D99982',
                fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit',
                minHeight: 32, whiteSpace: 'nowrap',
              }}
            >
              <XMarkIcon style={{ width: 14, height: 14 }} />
              Clear
            </button>
          )}
        </Box>

        {/* Collapsible: dates, search, dropdowns, Export, Refresh */}
        <Collapse in={showFilters} timeout="auto">
          {/* Date range quick chips */}
          <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {([
              { label: 'This Month', ...thisMonthRange() },
              { label: 'Last Month', ...lastMonthRange() },
              { label: 'All Time', dateFrom: '', dateTo: '' },
            ] as { label: string; dateFrom: string; dateTo: string }[]).map((range) => {
              const isActive = filters.dateFrom === range.dateFrom && filters.dateTo === range.dateTo;
              return (
                <Chip
                  key={range.label}
                  label={range.label}
                  onClick={() => setFilters(prev => ({ ...prev, dateFrom: range.dateFrom, dateTo: range.dateTo }))}
                  sx={{
                    fontWeight: 600, fontSize: '0.75rem', minHeight: 32,
                    backgroundColor: isActive ? `${theme.primary}20` : theme.gray?.[100] || '#F0F0F0',
                    color: isActive ? theme.primary : theme.gray?.[500] || '#666',
                    border: isActive ? `1.5px solid ${theme.primary}` : `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                    cursor: 'pointer', '&:hover': { opacity: 0.85 },
                  }}
                />
              );
            })}
            {filters.dateFrom && filters.dateTo && (
              <Typography variant="caption" sx={{ color: theme.gray?.[400], ml: 1 }}>
                {filters.dateFrom} → {filters.dateTo}
              </Typography>
            )}
          </Box>

          {/* From / To date pickers */}
          <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1, display: 'flex' }}>
            <FinanceDateRange
              from={filters.dateFrom}
              to={filters.dateTo}
              onFromChange={(v) => handleFilterChange('dateFrom', v)}
              onToChange={(v) => handleFilterChange('dateTo', v)}
              showLabels
            />
          </Box>

          {/* Search supplier + dropdown filters */}
          <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1 }}>
            <SearchFilterBar
              searchPlaceholder="Search supplier..."
              searchValue={filters.supplierSearch}
              onSearchChange={(value) => handleFilterChange('supplierSearch', value)}
              filters={expandedFilterConfigs}
            />
          </Box>

          {/* Action buttons */}
          <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', gap: 1, justifyContent: { xs: 'stretch', sm: 'flex-end' }, flexWrap: 'wrap' }}>
            <button
              onClick={handleExportCsv}
              disabled={exporting}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 10,
                border: '1.5px solid #C8C5BD', backgroundColor: 'var(--bg-surface)', /* basil-todo */
                color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.813rem',
                cursor: 'pointer', fontFamily: 'inherit', opacity: exporting ? 0.5 : 1,
                whiteSpace: 'nowrap', flex: '1 1 auto', minHeight: 40,
              }}
            >
              <ArrowDownTrayIcon style={{ width: 18, height: 18 }} />
              Export
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 10,
                border: `1.5px solid ${theme.primary || 'var(--action)'}50`,
                backgroundColor: `${theme.primary || 'var(--action)'}10`,
                color: theme.primary || 'var(--action)',
                fontWeight: 600, fontSize: '0.813rem', cursor: 'pointer', fontFamily: 'inherit',
                opacity: refreshing ? 0.5 : 1,
                whiteSpace: 'nowrap', flex: '1 1 auto', minHeight: 40,
              }}
            >
              <ArrowPathIcon style={{ width: 18, height: 18 }} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
          </Box>
        </Collapse>
      </Paper>

      {/* Filter-aware totals — directly under the filter row, always visible
          on mobile (replaces the old desktop-styled summary Papers) */}
      <FilteredTotals
        data-testid="expense-filtered-totals"
        items={[
          {
            label: hasActiveFilters ? 'Filtered Total' : 'Total Expenses',
            value: formatCurrency(displayedExpenseSum, { showDecimals: false }),
          },
          { label: 'Count', value: String(displayedExpenseTotal) },
        ]}
      />

      {/* Unspecified drill-down banner — set from /vendor/finance dashboard
          Top Payees "Unspecified" bar. Guides user to assign supplier/category
          inline so future reports stop bucketing this spend as "Unspecified". */}
      {filters.unspecified && (
        <Paper
          sx={{
            mb: 2,
            p: { xs: 1.5, sm: 2 },
            borderRadius: '12px',
            backgroundColor: `${theme.warning || '#E0A458'}15`,
            border: `1px solid ${theme.warning || '#E0A458'}40`,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
            <ExclamationTriangleIcon
              style={{ width: 20, height: 20, color: theme.warning || '#E0A458', flexShrink: 0, marginTop: 2 }}
            />
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: theme.text, mb: 0.25 }}>
                Showing expenses without a supplier
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: theme.gray?.[500] || '#666' }}>
                Click a row to assign a supplier — Top Payees will then show the real payee instead of "Unspecified".
              </Typography>
            </Box>
          </Box>
          <Chip
            label="Clear filter"
            size="small"
            onClick={() => handleFilterChange('unspecified', false)}
            onDelete={() => handleFilterChange('unspecified', false)}
            deleteIcon={<XMarkIcon style={{ width: 14, height: 14 }} />}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              backgroundColor: theme.components?.card?.background || '#FFF',
              border: `1px solid ${theme.warning || '#E0A458'}60`,
              color: theme.warning || '#E0A458',
              alignSelf: { xs: 'flex-end', sm: 'auto' },
            }}
          />
        </Paper>
      )}

      {/* Expense List */}
      {isMobile ? (
        /* Mobile: Card layout */
        <Paper
          sx={{
            borderRadius: '12px',
            backgroundColor: theme.components?.card?.background || '#FFFFFF',
            border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.5 }}>
            {loading ? (
              renderSkeletonCards()
            ) : displayedExpenses.length === 0 ? (
              <EmptyState
                icon={<ReceiptPercentIcon style={{ width: 44, height: 44, color: 'var(--text-tertiary)' }} />}
                title="No expenses yet"
                subtitle="Tap + to add your first expense"
                actionButton={
                  <PrimaryButton
                    startIcon={<PlusIcon style={{ width: 18, height: 18 }} />}
                    onClick={handleOpenAdd}
                    sx={{ borderRadius: '10px' }}
                  >
                    Add Expense
                  </PrimaryButton>
                }
              />
            ) : (
              displayedExpenses.map((expense) => {
                const statusInfo = PAYMENT_STATUS_MAP[expense.paymentStatus] || { label: expense.paymentStatus, variant: 'info' as const };
                return (
                  <Paper
                    key={expense.id}
                    onClick={isFinanceOnlyRestricted ? undefined : () => handleOpenEdit(expense)}
                    sx={{
                      p: 2,
                      mb: 1.5,
                      borderRadius: '10px',
                      backgroundColor: theme.components?.card?.background || '#FFFFFF',
                      border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:active': { transform: 'scale(0.98)' },
                      '&:hover': { borderColor: theme.primary + '66' },
                    }}
                    elevation={0}
                  >
                    {/* Top row: category + amount */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Chip
                          label={getCategoryName(expense)}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            backgroundColor: theme.button.primary.bg,
                            color: theme.button.primary.text,
                            height: 22,
                            mb: 0.5,
                          }}
                        />
                        {expense.supplierName && (
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ fontWeight: 500, color: theme.text, fontSize: '0.875rem' }}
                          >
                            {expense.supplierName}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: theme.text,
                          fontSize: '1.1rem',
                          flexShrink: 0,
                          ml: 1,
                        }}
                      >
                        {formatCurrency(expense.amount, { showDecimals: false })}
                      </Typography>
                    </Box>

                    {/* Bottom row: date + status + mode + actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666' }}>
                          {formatDate(expense.date)}
                        </Typography>
                        <StatusChip
                          status={statusInfo.label}
                          variant={statusInfo.variant}
                          sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                        <Chip
                          label={getModeLabel(expense.paymentMode)}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.65rem',
                            backgroundColor: theme.gray?.[100] || '#F0F0F0',
                            color: theme.gray?.[500] || '#666',
                            height: 20,
                          }}
                        />
                      </Box>

                      {/* Delete button - stop propagation. Hidden for
                          finance-only-restricted stewards. */}
                      {!isFinanceOnlyRestricted && (
                        <AppIconButton
                          icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                          tooltip="Delete"
                          variant="delete"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(expense);
                          }}
                          sx={{ width: 32, height: 32 }}
                        />
                      )}
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>

          {/* Mobile pagination */}
          {displayedExpenseTotal > 0 && !loading && (
            <Box
              sx={{
                borderTop: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.5,
                py: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666' }}>
                {`${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, displayedExpenseTotal)} of ${displayedExpenseTotal}`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <AppIconButton
                  icon={<span style={{ fontSize: 18, lineHeight: 1 }}>{'‹'}</span>}
                  tooltip="Previous"
                  variant="ghost"
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                />
                <AppIconButton
                  icon={<span style={{ fontSize: 18, lineHeight: 1 }}>{'›'}</span>}
                  tooltip="Next"
                  variant="ghost"
                  size="small"
                  disabled={(page + 1) * rowsPerPage >= displayedExpenseTotal}
                  onClick={() => setPage(page + 1)}
                />
              </Box>
            </Box>
          )}
        </Paper>
      ) : (
        /* Desktop: DataTable */
        <DataTable<Expense>
          columns={expenseColumns}
          data={displayedExpenses}
          loading={loading}
          rowKey={(row) => row.id}
          onRowClick={isFinanceOnlyRestricted ? undefined : (row) => handleOpenEdit(row)}
          emptyState={
            <EmptyState
              icon={<ReceiptPercentIcon style={{ width: 40, height: 40, color: 'var(--text-tertiary)' }} />}
              title="No expenses yet"
              subtitle='Click "Add Expense" to record your first expense'
            />
          }
          pagination={{
            page,
            rowsPerPage,
            total: displayedExpenseTotal,
            onPageChange: setPage,
            onRowsPerPageChange: (rpp) => { setRowsPerPage(rpp); setPage(0); },
            rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          }}
          sx={{ borderRadius: '12px', ...denseTableSx }}
        />
      )}

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          onClick={handleOpenAdd}
          aria-label="Add Expense"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 20,
            zIndex: 1000,
            backgroundColor: theme.button?.primary?.bg || 'var(--action)',
            color: theme.button?.primary?.text || '#FFFFFF',
            width: 56,
            height: 56,
            boxShadow: '0 4px 16px color-mix(in srgb, var(--action) 40%, transparent)',
            '&:hover': {
              backgroundColor: theme.button?.primary?.hover || 'var(--action-hover)',
              boxShadow: '0 6px 20px color-mix(in srgb, var(--action) 50%, transparent)',
            },
          }}
        >
          <PlusIcon style={{ width: 28, height: 28 }} />
        </Fab>
      )}
      </>
      )}

      {/* --- Voids & Comps Tab --- */}
      {activeTab === 'voids' && (
      <>
        {/* NRC Summary */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            mb: { xs: 2, sm: 3 },
            flexWrap: 'nowrap',
          }}
        >
          {Object.entries(NRC_TYPE_STYLES).map(([key, style]) => (
            <Paper
              key={key}
              sx={{
                flex: 1,
                p: { xs: 1.5, sm: 2 },
                borderRadius: '12px',
                backgroundColor: theme.components?.card?.background || '#FFFFFF',
                border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                {React.createElement(style.icon, { style: { width: 14, height: 14, color: style.color } })}
                <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.05em' }}>
                  {style.label}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: style.color,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  lineHeight: 1.2,
                }}
              >
                {formatCurrency(nrcSummary.byType[key] || 0, { showDecimals: false })}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Desktop Add button */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end', mb: 2 }}>
          <PrimaryButton
            startIcon={<PlusIcon style={{ width: 20, height: 20 }} />}
            onClick={() => setNrcFormOpen(true)}
            sx={{
              borderRadius: '10px',
              boxShadow: '0 2px 8px color-mix(in srgb, var(--action) 30%, transparent)',
              '&:hover': {
                boxShadow: '0 4px 12px color-mix(in srgb, var(--action) 40%, transparent)',
              },
            }}
          >
            Add Void / Comp
          </PrimaryButton>
        </Box>

        {/* NRC List */}
        <Paper
          sx={{
            borderRadius: '12px',
            backgroundColor: theme.components?.card?.background || '#FFFFFF',
            border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
            overflow: 'hidden',
          }}
        >
          {nrcLoading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Paper
                  key={i}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: '10px',
                    backgroundColor: theme.components?.card?.background || '#FFFFFF',
                    border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Skeleton variant="rounded" width={80} height={22} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
                    <Skeleton variant="rounded" width={70} height={24} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
                  </Box>
                  <Skeleton variant="text" width={140} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
                </Paper>
              ))}
            </Box>
          ) : nrcList.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <EmptyState
                icon={<NoSymbolIcon style={{ width: 44, height: 44, color: 'var(--text-tertiary)' }} />}
                title="No voids or comps recorded"
                subtitle="Record voids, complimentary orders, and staff meals here"
                actionButton={
                  <PrimaryButton
                    startIcon={<PlusIcon style={{ width: 18, height: 18 }} />}
                    onClick={() => setNrcFormOpen(true)}
                    sx={{ borderRadius: '10px' }}
                  >
                    Add Void / Comp
                  </PrimaryButton>
                }
              />
            </Box>
          ) : isMobile ? (
            /* Mobile: Card layout */
            <Box sx={{ p: 1.5 }}>
              {nrcList.map((nrc) => {
                const style = NRC_TYPE_STYLES[nrc.type] || { label: nrc.type, color: 'var(--text-tertiary)', icon: NoSymbolIcon };
                return (
                  <Paper
                    key={nrc.id}
                    sx={{
                      p: 2,
                      mb: 1.5,
                      borderRadius: '10px',
                      backgroundColor: theme.components?.card?.background || '#FFFFFF',
                      border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                    }}
                    elevation={0}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                      <Chip
                        icon={React.createElement(style.icon, { style: { width: 14, height: 14 } })}
                        label={style.label}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          backgroundColor: `${style.color}18`,
                          color: style.color,
                          height: 24,
                          '& .MuiChip-icon': { color: style.color },
                        }}
                      />
                      <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1.05rem' }}>
                        {formatCurrency(nrc.estimatedValue, { showDecimals: false })}
                      </Typography>
                    </Box>
                    {nrc.reason && (
                      <Typography variant="body2" sx={{ color: theme.text, fontSize: '0.813rem', mb: 0.5 }}>
                        {nrc.reason}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.7rem' }}>
                      {(() => {
                        try {
                          return new Date(nrc.date).toLocaleDateString(CURRENCY.locale, { day: '2-digit', month: 'short', year: 'numeric' });
                        } catch {
                          return nrc.date;
                        }
                      })()}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            /* Desktop: Table layout */
            <DataTable<NonRevenueCost>
              columns={[
                {
                  id: 'date',
                  label: 'Date',
                  render: (row) => (
                    <Typography sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem', whiteSpace: 'nowrap' }}>
                      {(() => {
                        try {
                          return new Date(row.date).toLocaleDateString(CURRENCY.locale, { day: '2-digit', month: 'short', year: 'numeric' });
                        } catch {
                          return row.date;
                        }
                      })()}
                    </Typography>
                  ),
                },
                {
                  id: 'type',
                  label: 'Type',
                  render: (row) => {
                    const style = NRC_TYPE_STYLES[row.type] || { label: row.type, color: 'var(--text-tertiary)', icon: NoSymbolIcon };
                    return (
                      <Chip
                        icon={React.createElement(style.icon, { style: { width: 14, height: 14 } })}
                        label={style.label}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          backgroundColor: `${style.color}18`,
                          color: style.color,
                          height: 24,
                          '& .MuiChip-icon': { color: style.color },
                        }}
                      />
                    );
                  },
                },
                {
                  id: 'value',
                  label: 'Value',
                  align: 'right',
                  render: (row) => (
                    <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                      {formatCurrency(row.estimatedValue, { showDecimals: false })}
                    </Typography>
                  ),
                },
                {
                  id: 'reason',
                  label: 'Reason',
                  render: (row) =>
                    row.reason ? (
                      <Typography sx={{ color: theme.text, fontSize: '0.813rem' }}>{row.reason}</Typography>
                    ) : (
                      <Typography component="span" sx={{ color: theme.gray?.[300] || '#CCC', fontStyle: 'italic', fontSize: '0.813rem' }}>
                        --
                      </Typography>
                    ),
                },
              ]}
              data={nrcList}
              rowKey={(row) => row.id}
              sx={{ border: 'none', boxShadow: 'none', borderRadius: 0, ...denseTableSx }}
            />
          )}
        </Paper>

        {/* Mobile FAB for Voids tab */}
        {isMobile && (
          <Fab
            onClick={() => setNrcFormOpen(true)}
            aria-label="Add Void / Comp"
            sx={{
              position: 'fixed',
              bottom: 80,
              right: 20,
              zIndex: 1000,
              backgroundColor: theme.button?.primary?.bg || 'var(--action)',
              color: theme.button?.primary?.text || '#FFFFFF',
              width: 56,
              height: 56,
              boxShadow: '0 4px 16px color-mix(in srgb, var(--action) 40%, transparent)',
              '&:hover': {
                backgroundColor: theme.button?.primary?.hover || 'var(--action-hover)',
                boxShadow: '0 6px 20px color-mix(in srgb, var(--action) 50%, transparent)',
              },
            }}
          >
            <PlusIcon style={{ width: 28, height: 28 }} />
          </Fab>
        )}
      </>
      )}

      {/* Recurring Tab Content */}
      {activeTab === 'recurring' && (
        <Paper
          sx={{
            borderRadius: '12px',
            backgroundColor: theme.components?.card?.background || '#FFFFFF',
            border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
              Recurring Expenses
            </Typography>
            <button
              onClick={fetchRecurringTemplates}
              disabled={recurringLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 8,
                border: `1.5px solid ${theme.primary || 'var(--action)'}50`,
                backgroundColor: `${theme.primary || 'var(--action)'}10`,
                color: theme.primary || 'var(--action)',
                fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit',
                opacity: recurringLoading ? 0.5 : 1,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              <ArrowPathIcon style={{ width: 14, height: 14 }} className={recurringLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </Box>

          {recurringLoading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Box key={i} sx={{ mb: 1.5, display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Skeleton variant="rounded" width={120} height={24} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
                  <Skeleton variant="text" sx={{ flex: 1, bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
                  <Skeleton variant="rounded" width={80} height={28} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
                </Box>
              ))}
            </Box>
          ) : recurringList.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: theme.text, mb: 0.5 }}>
                No recurring expenses
              </Typography>
              <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem' }}>
                Set up repeating expenses when adding a new expense
              </Typography>
            </Box>
          ) : (
            <Box sx={{ px: { xs: 0.5, sm: 1 }, pb: 1.5 }}>
              {recurringList.map((tmpl) => {
                const statusColor =
                  tmpl.status === 'ACTIVE' ? (theme.success || '#4CAF50') :
                  tmpl.status === 'PAUSED' ? (theme.warning || '#FF9800') :
                  (theme.gray?.[400] || '#999');

                const freqLabel =
                  tmpl.frequency === 'WEEKLY' ? 'Weekly' :
                  tmpl.frequency === 'BI_WEEKLY' ? 'Bi-weekly' : 'Monthly';

                return (
                  <Box
                    key={tmpl.id}
                    sx={{
                      mx: { xs: 1, sm: 1 },
                      mb: 1,
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: '10px',
                      border: `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
                      backgroundColor: tmpl.status === 'CANCELLED' ? `${theme.gray?.[100] || '#F5F5F5'}` : 'transparent',
                    }}
                  >
                    {/* Row 1: Name + Amount */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '0.9rem', lineHeight: 1.3 }}>
                          {tmpl.description || tmpl.supplierName || tmpl.categoryName}
                        </Typography>
                        {tmpl.supplierName && tmpl.description && (
                          <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.72rem' }}>
                            {tmpl.supplierName}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1rem', flexShrink: 0, ml: 1 }}>
                        {formatCurrency(tmpl.amount, { showDecimals: false })}
                      </Typography>
                    </Box>

                    {/* Row 2: Badges */}
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                      <Chip
                        label={freqLabel}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 22,
                          backgroundColor: theme.button.primary.bg,
                          color: theme.button.primary.text,
                        }}
                      />
                      <StatusChip
                        status={tmpl.status.charAt(0) + tmpl.status.slice(1).toLowerCase()}
                        variant={tmpl.status === 'ACTIVE' ? 'success' : tmpl.status === 'PAUSED' ? 'warning' : 'error'}
                        sx={{
                          fontSize: '0.65rem',
                          height: 22,
                          backgroundColor: `${statusColor}18`,
                          color: statusColor,
                          border: `1px solid ${statusColor}40`,
                        }}
                      />
                      <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.72rem' }}>
                        {tmpl.categoryName}
                      </Typography>
                      {tmpl.nextDueDate && tmpl.status === 'ACTIVE' && (
                        <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#999', fontSize: '0.68rem', ml: 'auto' }}>
                          Next: {(() => {
                            try {
                              return new Date(tmpl.nextDueDate).toLocaleDateString(CURRENCY.locale, { day: '2-digit', month: 'short' });
                            } catch { return tmpl.nextDueDate; }
                          })()}
                        </Typography>
                      )}
                    </Box>

                    {/* Row 3: Actions */}
                    {tmpl.status !== 'CANCELLED' && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {tmpl.status === 'ACTIVE' && (
                          <button
                            onClick={() => handleRecurringAction('pause', tmpl.id)}
                            style={{
                              padding: '6px 14px',
                              borderRadius: 8,
                              border: `1px solid ${theme.warning || '#FF9800'}60`,
                              backgroundColor: `${theme.warning || '#FF9800'}10`,
                              color: theme.warning || '#FF9800',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              minHeight: 34,
                            }}
                          >
                            Pause
                          </button>
                        )}
                        {tmpl.status === 'PAUSED' && (
                          <button
                            onClick={() => handleRecurringAction('resume', tmpl.id)}
                            style={{
                              padding: '6px 14px',
                              borderRadius: 8,
                              border: `1px solid ${theme.success || '#4CAF50'}60`,
                              backgroundColor: `${theme.success || '#4CAF50'}10`,
                              color: theme.success || '#4CAF50',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              minHeight: 34,
                            }}
                          >
                            Resume
                          </button>
                        )}
                        <button
                          onClick={() => handleRecurringAction('cancel', tmpl.id)}
                          style={{
                            padding: '6px 14px',
                            borderRadius: 8,
                            border: `1px solid ${theme.danger || '#D99982'}60`,
                            backgroundColor: 'transparent',
                            color: theme.danger || '#D99982',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            minHeight: 34,
                          }}
                        >
                          Cancel
                        </button>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      )}

      {/* Expense Form Modal */}
      <ExpenseForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingExpense(null);
        }}
        onSave={handleSave}
        expense={editingExpense}
        categories={expenseFormCategories}
        staffMembers={staffMembers}
        onCategoryCreated={fetchCategories}
      />

      {/* Non-Revenue Cost Form Modal */}
      <NonRevenueCostForm
        open={nrcFormOpen}
        onClose={() => setNrcFormOpen(false)}
        onSaved={fetchNonRevenueCosts}
      />

      {/* Invoice Scanner Modal */}
      <InvoiceScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onResult={handleScanResult}
      />

      {/* OCR Review Form Modal */}
      <OcrReviewForm
        open={reviewOpen}
        onClose={() => { setReviewOpen(false); setOcrData(null); }}
        ocrData={ocrData}
        categories={categories}
        onSave={handleOcrSaved}
      />
    </Box>
  );
};

export default ExpenseManagement;
